/**
 * Khung "xem trước" ngành ở Cấu hình → Thông tin tiệm (v0.23.0, docs/plans/mau-nganh-hai-app.md mục 3.5): dải bìa + biểu tượng
 * + nút giả "Đặt lịch" mang màu ngành đang CHỌN. Màu đặt lên CHÍNH khung (không lên <html>) nên trang không đổi màu cho tới khi
 * lưu. Dùng chung `bienCssNganh` với web quầy thật và Mini App; biểu tượng cùng nguồn `HINH_NGANH` với Mini App.
 * Kế hoạch ảnh bìa + logo (v0.23.1) chỉ MỞ RỘNG khung này, không dựng khung thứ hai.
 */
import {
  apBienCss,
  bienCssNganh,
  boBienCss,
  HINH_NGANH,
  khoaPhanHinh,
  NGANH,
  type NganhTiem,
} from '@sapa/shared'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { chuCaiLogo } from './anh-cua-hang.ts'
import { duongDanMayChu } from './api.ts'
import { TT } from './text/thong-tin-tiem.ts'

/** Biểu tượng ngành (nét đơn, lưới 24) — cùng dữ liệu với `BieuTuongNganh` của Mini App. */
export function BieuTuongNganhQuay({ nganh, co = 40 }: { nganh: NganhTiem; co?: number }) {
  return (
    <svg width={co} height={co} viewBox="0 0 24 24" aria-hidden="true">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {HINH_NGANH[NGANH[nganh].bieuTuong].map((p) => {
          const khoa = khoaPhanHinh(p)
          if (p.k === 'circle') return <circle key={khoa} cx={p.cx} cy={p.cy} r={p.r} />
          if (p.k === 'rect')
            return <rect key={khoa} x={p.x} y={p.y} width={p.width} height={p.height} rx={p.rx} />
          return <path key={khoa} d={p.d} />
        })}
      </g>
    </svg>
  )
}

/**
 * v0.23.3 (ảnh bìa + logo): bản ảnh chỉ MỞ RỘNG khung này, không dựng khung thứ hai. Khi `anhBia` có (đường dẫn máy chủ đã kiểm dạng) ảnh phủ kín
 * dải bìa thay cho dải màu + biểu tượng ngành; `ten` (kèm `logo` hoặc không) thêm ô logo 48×48 — ảnh nếu có, không thì chữ cái đầu của tên tiệm.
 * Ảnh lỗi tải → về dải màu / chữ cái, không ô vỡ. Không truyền gì → hệt bản v0.23.0 (tiệm chưa mở tính năng ảnh thấy y như cũ).
 */
export function XemTruocNganh({
  nganh,
  anhBia = null,
  logo = null,
  ten,
}: {
  nganh: NganhTiem
  anhBia?: string | null
  logo?: string | null
  ten?: string
}) {
  const khungRef = useRef<HTMLDivElement>(null)
  const [biaHong, setBiaHong] = useState(false)
  const [logoHong, setLogoHong] = useState(false)
  useLayoutEffect(() => {
    const el = khungRef.current
    if (el === null) return
    const bien = bienCssNganh(nganh)
    apBienCss(el, bien)
    return () => boBienCss(el, bien)
  }, [nganh])
  // Đổi ảnh (đường dẫn mới) thì thử tải lại; ảnh cũ hỏng không được "dính" sang ảnh mới.
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy lại đúng khi đổi đường dẫn ảnh.
  useEffect(() => setBiaHong(false), [anhBia])
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy lại đúng khi đổi đường dẫn logo.
  useEffect(() => setLogoHong(false), [logo])
  const coBia = anhBia !== null && !biaHong
  const coLogo = ten !== undefined || logo !== null
  return (
    <div className="xem-truoc-nganh" ref={khungRef} data-nganh={nganh}>
      <p className="nhan">{coBia || logo !== null ? TT.xemTruocCoAnh : TT.xemTruoc}</p>
      <div className="xtn-khung">
        <div className={`xtn-bia ${coBia ? 'co-anh' : ''}`}>
          {coBia ? (
            <img
              className="xtn-anh"
              src={duongDanMayChu(anhBia)}
              alt=""
              onError={() => setBiaHong(true)}
            />
          ) : (
            <BieuTuongNganhQuay nganh={nganh} />
          )}
        </div>
        <div className={`xtn-day ${coLogo ? 'co-logo' : ''}`}>
          {coLogo && (
            <span className="xtn-logo" aria-hidden="true">
              {logo !== null && !logoHong ? (
                <img src={duongDanMayChu(logo)} alt="" onError={() => setLogoHong(true)} />
              ) : (
                chuCaiLogo(ten ?? '')
              )}
            </span>
          )}
          {/* Nút giả: chỉ để thấy nút chính của khách mang màu nào — không bấm được, không đọc ra bằng trình đọc màn hình. */}
          <span className="xtn-nut" aria-hidden="true">
            {TT.nutGia}
          </span>
        </div>
      </div>
    </div>
  )
}
